import React, { useCallback, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { useLazyGetAdditionalObjectsQuery } from '../../../../../store/integrations/integrations'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { TestResultDisplay } from '../TestResultDisplay'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { IntegrationSettings } from '../../AdminSettings/schema'
import { useFormContext } from 'react-hook-form'
import { ContactDropdown } from '../../../../MeetingSummary/CRM/ContactDropdown'
import { SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import '../MappingTester.scss'

export interface AdditionalObjectsMappingTesterProps {
  serviceId: IntegrationServiceId
}

interface FetchedItem {
  label: string
  count: number
}

const extractFetchedItems = (data: Record<string, unknown>): FetchedItem[] => {
  const topLevelItems = Object.entries(data)
    .filter(([key, value]) => Array.isArray(value) && key !== 'household')
    .map(([key, value]) => ({ label: key, count: (value as unknown[]).length }))
  const householdData = data['household']
  const householdItems =
    householdData !== null && typeof householdData === 'object' && !Array.isArray(householdData)
      ? Object.entries(householdData as Record<string, unknown>)
          .filter(([, value]) => Array.isArray(value))
          .map(([key, value]) => ({ label: `Household ${key}`, count: (value as unknown[]).length }))
      : []
  return [...topLevelItems, ...householdItems]
}

export const AdditionalObjectsMappingTester: React.FC<AdditionalObjectsMappingTesterProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)

  const {
    formState: { isDirty, isValid },
    watch,
  } = useFormContext<IntegrationSettings>()
  const contactType = watch('mappings.contact.objectName')

  const isFormUnsaved = isDirty || !isValid
  const integrationName = getIntegrationDisplayName(serviceId)

  const [testSuccess, setTestSuccess] = useState(false)
  const [testError, setTestError] = useState(false)
  const [rawResult, setRawResult] = useState<unknown | null>(null)
  const [syncedItems, setSyncedItems] = useState<FetchedItem[]>([])
  const [selectedContactExternalId, setSelectedContactExternalId] = useState<string | null>(null)

  const [triggerGetAdditionalObjects, { isFetching: isRunningTest, isLoading: isAdditionalObjectsLoading }] = useLazyGetAdditionalObjectsQuery()

  const handleRunTest = useCallback(async () => {
    if (!selectedContactExternalId) {
      return
    }
    setTestSuccess(false)
    setTestError(false)
    setSyncedItems([])

    try {
      const result = await triggerGetAdditionalObjects({
        accountId,
        connector: serviceId,
        relatedTo: { id: selectedContactExternalId, type: contactType },
      }).unwrap()
      setRawResult(result)
      setTestSuccess(true)
      setSyncedItems(extractFetchedItems(result as Record<string, unknown>))
    } catch (err) {
      setTestError(true)
      setRawResult(err)
    }
  }, [selectedContactExternalId, accountId, serviceId, triggerGetAdditionalObjects, contactType])

  const onContactOptionSelected = useCallback((option: SelectOption) => {
    setSelectedContactExternalId(option.id)
  }, [])

  return (
    <>
      <ContactDropdown
        serviceId={serviceId}
        onContactOptionSelected={onContactOptionSelected}
        initialSelectedOptionExternalId={selectedContactExternalId ?? undefined}
      />

      <div className='border border-dark rounded-3 p-4 mt-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className='bi bi-sliders2 fs-5'></i>
              <h6 className='fw-bold mb-0'>Test AI Context Objects</h6>
            </div>
            <p className='text-secondary mb-0 small'>Verify that AI context objects are retrieved correctly as configured.</p>
          </div>
          <Tooltip id='additional-objects-run-test-tooltip' title={isFormUnsaved ? 'Save your changes before running a test' : undefined}>
            <Button
              type='button'
              variant='primaryGhost'
              className='d-flex align-items-center gap-2 fw-semibold flex-shrink-0'
              onClick={handleRunTest}
              disabled={isRunningTest || !selectedContactExternalId || isFormUnsaved}
            >
              {isRunningTest ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-play-fill' />}
              Run Test
            </Button>
          </Tooltip>
        </div>
        <TestResultDisplay
          isLoading={isAdditionalObjectsLoading}
          hasResult={testSuccess || testError}
          isSuccess={testSuccess}
          isError={testError}
          integrationName={integrationName}
          rawResult={rawResult}
          errorContent={
            <p className='mb-0 text-dark ps-4'>Failed to fetch additional objects from {integrationName}. Please verify your integration settings.</p>
          }
          successContent={
            syncedItems.length > 0 && (
              <ul className='mb-0 ps-4'>
                {syncedItems.map((item) => (
                  <li key={item.label} className='text-dark'>
                    {item.label} ({item.count}) successfully fetched
                  </li>
                ))}
              </ul>
            )
          }
        />
      </div>
    </>
  )
}
